import { useEffect, useId, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DEFAULT_SITE_THEME_ID,
  DEFAULT_SITE_MESSENGER_BUTTONS,
  normalizeSiteMessengerButtons,
  normalizeSiteThemeId,
  SITE_MESSENGER_CHANNELS,
  type SiteThemeId,
  type SiteMessengerButtons,
  type SiteMessengerChannel,
} from "@zapis24/types";
import { buildMasterLandingUrl } from "@zapis24/utils";
import { apiFetch } from "../api.js";

interface Business {
  name: string;
  slug: string;
  phone: string | null;
  tagline: string | null;
  description: string | null;
  siteThemeId?: SiteThemeId | null;
  siteMessengerButtons?: SiteMessengerButtons;
}

type SiteForm = {
  tagline: string;
  description: string;
  siteThemeId: SiteThemeId;
  siteMessengerButtons: SiteMessengerButtons;
};

const LANDING_BASE_URL = import.meta.env["VITE_LANDING_URL"] ?? "https://masters.zapis24.online";
const LANDING_LINK_FORMAT = import.meta.env["VITE_LANDING_LINK_FORMAT"] as string | undefined;
const LANDING_USE_SLUG_SUBDOMAINS =
  import.meta.env["VITE_LANDING_USE_SLUG_SUBDOMAINS"] as string | undefined;

const SITE_THEME_PREVIEWS: Array<{
  id: SiteThemeId;
  label: string;
  description: string;
  swatches: string[];
  previewImage: string;
  background: string;
  text: string;
  card: string;
  cta: string;
  outlineColor: string;
}> = [
  {
    id: "current-pink",
    label: "Розовый акцент",
    description: "Яркий beauty-акцент с темной основой.",
    swatches: ["#190d18", "#d94680", "#ffb9ce", "#273a32"],
    previewImage: "/theme-previews/current-pink.png",
    background: "linear-gradient(135deg,#190d18 0%,#33203a 48%,#e54786 100%)",
    text: "#ffffff",
    card: "#ffffff",
    cta: "#273a32",
    outlineColor: "#d94680",
  },
  {
    id: "masculine-graphite",
    label: "Графит и хвоя",
    description: "Сдержанный графит, хвоя и холодный свет.",
    swatches: ["#070a0d", "#315d72", "#a6b3af", "#17352b"],
    previewImage: "/theme-previews/masculine-graphite.png",
    background: "linear-gradient(135deg,#070a0d 0%,#14222b 48%,#335343 100%)",
    text: "#f7f4ec",
    card: "#f5f2ea",
    cta: "#17352b",
    outlineColor: "#315d72",
  },
  {
    id: "light-pearl",
    label: "Светлый жемчужный",
    description: "Светлая мягкая палитра с розовым и мятой.",
    swatches: ["#fff9fb", "#ffd6df", "#cce7dd", "#2c5d52"],
    previewImage: "/theme-previews/light-pearl.png",
    background: "linear-gradient(135deg,#fff9fb 0%,#f2eef5 48%,#dceee6 100%)",
    text: "#251822",
    card: "#ffffff",
    cta: "#2c5d52",
    outlineColor: "#2c5d52",
  },
  {
    id: "lagoon-coral",
    label: "Бирюза и коралл",
    description: "Глубокая бирюза с теплым коралловым светом.",
    swatches: ["#061c22", "#00a7a5", "#ff8a7a", "#0d4d57"],
    previewImage: "/theme-previews/lagoon-coral.png",
    background: "linear-gradient(135deg,#061c22 0%,#153746 48%,#d84f68 100%)",
    text: "#f8fffb",
    card: "#fbfbf6",
    cta: "#0d4d57",
    outlineColor: "#00a7a5",
  },
];

const MESSENGER_META: Record<
  SiteMessengerChannel,
  {
    label: string;
    placeholder: string;
    inputType: "url" | "tel";
    iconSrc?: string;
  }
> = {
  max: {
    label: "MAX",
    placeholder: "https://max.ru/u/...",
    inputType: "url",
    iconSrc: "/brand-icons/max-logo.png",
  },
  telegram: {
    label: "Telegram",
    placeholder: "https://t.me/username",
    inputType: "url",
  },
  whatsapp: {
    label: "WhatsApp",
    placeholder: "https://wa.me/79990000000",
    inputType: "url",
    iconSrc: "/brand-icons/whatsapp.jpg",
  },
  vk: {
    label: "VK",
    placeholder: "https://vk.com/username",
    inputType: "url",
  },
  instagram: {
    label: "Instagram",
    placeholder: "https://instagram.com/username",
    inputType: "url",
  },
  phone: {
    label: "Звонок",
    placeholder: "+7 900 000-00-00",
    inputType: "tel",
  },
};

type MessengerLinkInstruction = {
  channel: SiteMessengerChannel;
  title: string;
  description: string;
  example: string;
};

const MESSENGER_LINK_INSTRUCTIONS: MessengerLinkInstruction[] = [
  {
    channel: "max",
    title: "MAX",
    description: "MAX: вставьте ссылку профиля или чата.",
    example: "Пример: https://max.ru/u/username",
  },
  {
    channel: "telegram",
    title: "Telegram",
    description: "Telegram: вставьте ссылку на username.",
    example: "Пример: https://t.me/username",
  },
  {
    channel: "whatsapp",
    title: "WhatsApp",
    description: "WhatsApp: вставьте номер в международном формате.",
    example: "Пример: https://wa.me/79990000000",
  },
  {
    channel: "vk",
    title: "VK",
    description: "VK: вставьте ссылку страницы или сообщества.",
    example: "Пример: https://vk.com/username",
  },
  {
    channel: "instagram",
    title: "Instagram",
    description:
      "Instagram: откройте свой профиль, выберите «Поделиться профилем» или соберите ссылку из имени пользователя без @.",
    example: "Пример: https://instagram.com/username",
  },
  {
    channel: "phone",
    title: "Звонок",
    description:
      "Звонок: укажите номер, на который клиент сможет позвонить с сайта. Он может отличаться от номера для входа в личный кабинет.",
    example: "Пример: +7 900 000-00-00",
  },
];

function defaultButtons(): SiteMessengerButtons {
  return normalizeSiteMessengerButtons(DEFAULT_SITE_MESSENGER_BUTTONS);
}

function buildLandingUrl(slug: string): string {
  return buildMasterLandingUrl(slug, {
    baseUrl: LANDING_BASE_URL,
    format: LANDING_LINK_FORMAT,
    useSlugSubdomains: LANDING_USE_SLUG_SUBDOMAINS,
  });
}

function MessengerIcon({ channel }: { channel: SiteMessengerChannel }) {
  const gradientId = `site-messenger-${channel}-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const meta = MESSENGER_META[channel];

  if (meta.iconSrc) {
    return (
      <img
        aria-hidden="true"
        src={meta.iconSrc}
        alt=""
        loading="lazy"
        decoding="async"
        className="h-full w-full object-cover"
      />
    );
  }

  switch (channel) {
    case "telegram":
      return (
        <svg aria-hidden="true" className="h-full w-full" viewBox="0 0 48 48">
          <circle cx="24" cy="24" r="24" fill="#27A7E7" />
          <path
            d="M36.8 13.4 31.7 36c-.4 1.6-1.5 2-3 1.2l-7.9-5.8-3.8 3.7c-.4.4-.8.8-1.6.8l.6-8 14.7-13.3c.7-.6-.1-.9-1-.3L11.5 25.7 3.8 23.3c-1.7-.5-1.7-1.7.4-2.5l30.1-11.6c1.4-.5 2.7.3 2.5 4.2Z"
            fill="#fff"
          />
        </svg>
      );
    case "vk":
      return (
        <svg aria-hidden="true" className="h-full w-full" viewBox="0 0 48 48">
          <rect width="48" height="48" rx="13" fill="#0077FF" />
          <path
            d="M25.7 33.2c-8.3 0-13.1-5.7-13.4-15.2h4.2c.1 6.9 3.2 9.9 5.5 10.5V18h4v6c2.3-.2 4.7-3 5.5-6h4.2c-.7 3.7-3.5 6.6-5.4 7.8 1.9 1 5 3.5 6.2 7.4H32c-.9-2.8-3.1-5-6-5.3v5.3h-.3Z"
            fill="#fff"
          />
        </svg>
      );
    case "instagram":
      return (
        <svg aria-hidden="true" className="h-full w-full" viewBox="0 0 48 48">
          <defs>
            <radialGradient id={gradientId} cx="30%" cy="100%" r="115%">
              <stop offset="0%" stopColor="#FED576" />
              <stop offset="28%" stopColor="#F47133" />
              <stop offset="58%" stopColor="#BC2A8D" />
              <stop offset="100%" stopColor="#4C68D7" />
            </radialGradient>
          </defs>
          <rect width="48" height="48" rx="14" fill={`url(#${gradientId})`} />
          <rect
            x="12"
            y="12"
            width="24"
            height="24"
            rx="8"
            fill="none"
            stroke="#fff"
            strokeWidth="3.2"
          />
          <circle cx="24" cy="24" r="6.4" fill="none" stroke="#fff" strokeWidth="3.2" />
          <circle cx="32.2" cy="15.8" r="2" fill="#fff" />
        </svg>
      );
    case "phone":
      return (
        <svg aria-hidden="true" className="h-full w-full" viewBox="0 0 48 48">
          <defs>
            <radialGradient id={gradientId} cx="34%" cy="24%" r="86%">
              <stop offset="0%" stopColor="#FFD7B0" />
              <stop offset="42%" stopColor="#FF6B8A" />
              <stop offset="100%" stopColor="#B83268" />
            </radialGradient>
          </defs>
          <circle cx="24" cy="24" r="24" fill={`url(#${gradientId})`} />
          <circle cx="17" cy="15" r="7" fill="#fff" opacity="0.2" />
          <path
            d="M33.7 29.5c-1.6 0-3.2-.3-4.6-.9-.7-.3-1.5-.1-2 .4l-2 2c-3.5-1.9-6.2-4.6-8.1-8.1l2-2c.5-.5.7-1.3.4-2-.6-1.5-.9-3-.9-4.6 0-1-.8-1.8-1.8-1.8h-3.2c-1 0-1.9.8-1.9 1.8 0 12.2 9.9 22.1 22.1 22.1 1 0 1.8-.8 1.8-1.9v-3.2c0-1-.8-1.8-1.8-1.8Z"
            fill="#fff"
          />
        </svg>
      );
    case "max":
    case "whatsapp":
      return null;
  }
}

function isHttpUrl(value: string): boolean {
  return /^https?:\/\/[^\s]+$/i.test(value);
}

function isPhoneValue(value: string): boolean {
  const digits = value.replace(/\D/g, "");
  return digits.length >= 5 && digits.length <= 15;
}

export function SitePage({ token, onOpenSupport }: { token: string; onOpenSupport: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<SiteForm>({
    tagline: "",
    description: "",
    siteThemeId: DEFAULT_SITE_THEME_ID,
    siteMessengerButtons: defaultButtons(),
  });
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [previewThemeId, setPreviewThemeId] = useState<SiteThemeId | null>(null);
  const [messengerHelpChannel, setMessengerHelpChannel] = useState<SiteMessengerChannel | null>(null);

  const { data: business, isLoading } = useQuery({
    queryKey: ["business-me"],
    queryFn: () => apiFetch<Business>(token, "/api/business/me"),
  });

  useEffect(() => {
    if (!business) return;
    const siteMessengerButtons = normalizeSiteMessengerButtons(business.siteMessengerButtons);
    if (business.siteMessengerButtons?.phone === undefined && business.phone?.trim()) {
      siteMessengerButtons.phone = {
        enabled: true,
        url: business.phone.trim(),
      };
    }

    setForm({
      tagline: business.tagline ?? "",
      description: business.description ?? "",
      siteThemeId: normalizeSiteThemeId(business.siteThemeId),
      siteMessengerButtons,
    });
  }, [business]);

  useEffect(() => {
    if (!previewThemeId && !messengerHelpChannel) return;

    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setPreviewThemeId(null);
        setMessengerHelpChannel(null);
      }
    }

    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [messengerHelpChannel, previewThemeId]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const tagline = form.tagline.trim();
      const description = form.description.trim();

      if (!tagline) {
        throw new Error("Заполните заголовок сайта.");
      }

      if (!description) {
        throw new Error("Заполните подзаголовок сайта.");
      }

      for (const channel of SITE_MESSENGER_CHANNELS) {
        const button = form.siteMessengerButtons[channel];
        const url = button.url.trim();
        const meta = MESSENGER_META[channel];

        if (url && meta.inputType === "tel" && !isPhoneValue(url)) {
          throw new Error("Телефон для звонков должен содержать корректный номер.");
        }

        if (url && meta.inputType === "url" && !isHttpUrl(url)) {
          throw new Error(`Ссылка для ${meta.label} должна начинаться с http:// или https://.`);
        }
      }

      await apiFetch(token, "/api/business/me", {
        method: "PATCH",
        body: JSON.stringify({
          tagline,
          description,
          siteThemeId: form.siteThemeId,
          siteMessengerButtons: form.siteMessengerButtons,
        }),
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["business-me"] });
      setSaved(true);
      setError("");
      setTimeout(() => setSaved(false), 3000);
    },
    onError: (e: Error) => {
      setSaved(false);
      setError(e.message);
    },
  });

  function updateMessenger(
    channel: SiteMessengerChannel,
    patch: Partial<SiteMessengerButtons[SiteMessengerChannel]>,
  ) {
    setSaved(false);
    setError("");
    setForm((current) => ({
      ...current,
      siteMessengerButtons: {
        ...current.siteMessengerButtons,
        [channel]: {
          ...current.siteMessengerButtons[channel],
          ...patch,
        },
      },
    }));
  }

  const enabledButtons = SITE_MESSENGER_CHANNELS.filter((channel) => {
    const button = form.siteMessengerButtons[channel];
    return button.enabled;
  });
  const siteUrl = business?.slug ? buildLandingUrl(business.slug) : "";
  const selectedTheme =
    SITE_THEME_PREVIEWS.find((theme) => theme.id === form.siteThemeId) ?? SITE_THEME_PREVIEWS[0]!;
  const previewTheme = previewThemeId
    ? SITE_THEME_PREVIEWS.find((theme) => theme.id === previewThemeId) ?? null
    : null;
  const activeMessengerHelpInstruction = messengerHelpChannel
    ? MESSENGER_LINK_INSTRUCTIONS.find((instruction) => instruction.channel === messengerHelpChannel) ?? null
    : null;

  function selectTheme(siteThemeId: SiteThemeId) {
    setSaved(false);
    setError("");
    setForm((current) => ({ ...current, siteThemeId }));
  }

  if (isLoading) {
    return <div className="p-6 text-center text-sm text-gray-400">Загрузка...</div>;
  }

  return (
    <div className="mx-auto w-full max-w-5xl min-w-0 p-4 pb-24">
      <div className="mb-5 flex min-w-0 flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-lg font-bold text-gray-800">Редактор сайта</h2>
          <p className="mt-1 text-sm leading-5 text-gray-500">
            Первый экран сайта, оформление и кнопки мессенджеров для клиентов.
          </p>
        </div>
        {siteUrl && (
          <a
            href={siteUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center rounded-lg border border-primary/20 bg-white px-3 py-2 text-sm font-semibold text-primary shadow-sm transition hover:bg-primary/5"
          >
            Открыть сайт
          </a>
        )}
      </div>

      <p className="mb-4 rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-800">
        Чтобы увидеть изменения на уже открытом сайте, обновите страницу сайта после сохранения.
      </p>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          saveMutation.mutate();
        }}
        className="grid w-full min-w-0 gap-4 lg:grid-cols-[minmax(0,1fr)_360px]"
      >
        <div className="min-w-0 space-y-4">
          <Section title="Первый экран">
            <Field label="Заголовок сайта">
              <textarea
                required
                value={form.tagline}
                onChange={(event) => {
                  setSaved(false);
                  setForm({ ...form, tagline: event.target.value });
                }}
                className="input min-h-24 resize-none text-base font-semibold"
                maxLength={120}
                placeholder="Красота начинается с деталей"
              />
            </Field>
            <Field label="Подзаголовок">
              <textarea
                required
                value={form.description}
                onChange={(event) => {
                  setSaved(false);
                  setForm({ ...form, description: event.target.value });
                }}
                className="input min-h-24 resize-none"
                maxLength={320}
                placeholder="Маникюр и педикюр: запишитесь онлайн на подходящую услугу и выберите удобное время."
              />
            </Field>
          </Section>

          <Section title="Цветовое оформление">
            <p className="-mt-1 text-xs leading-5 text-gray-500">
              Нажмите «Посмотреть пример», чтобы открыть большой скриншот сайта. Выбранная схема применится после сохранения.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              {SITE_THEME_PREVIEWS.map((theme) => {
                const isSelected = form.siteThemeId === theme.id;

                return (
                  <div
                    key={theme.id}
                    data-testid={`site-theme-card-${theme.id}`}
                    className="rounded-xl border-2 p-3 transition hover:-translate-y-0.5"
                    style={{
                      backgroundColor: isSelected ? `${theme.outlineColor}0f` : "#ffffff",
                      borderColor: isSelected ? theme.outlineColor : `${theme.outlineColor}66`,
                      boxShadow: isSelected
                        ? `0 0 0 3px ${theme.outlineColor}24, 0 14px 30px ${theme.outlineColor}1f`
                        : `0 0 0 1px ${theme.outlineColor}20, 0 10px 22px rgba(15, 23, 42, 0.07)`,
                    }}
                  >
                    <label className="block min-w-0 cursor-pointer">
                      <input
                        type="radio"
                        name="siteThemeId"
                        value={theme.id}
                        checked={isSelected}
                        onChange={() => selectTheme(theme.id)}
                        className="sr-only"
                      />
                      <span
                        className="block overflow-hidden rounded-lg p-3 shadow-sm"
                        style={{ background: theme.background, color: theme.text }}
                      >
                        <span className="block break-words text-sm font-black leading-tight">{theme.label}</span>
                        <span className="mt-6 block rounded-lg p-2 text-xs font-semibold shadow-sm" style={{ backgroundColor: theme.card, color: "#20151d" }}>
                          Быстрая запись
                          <span
                            className="mt-2 block w-fit rounded-full px-3 py-1 text-[11px] font-black text-white"
                            style={{ backgroundColor: theme.cta }}
                          >
                            Записаться
                          </span>
                        </span>
                      </span>
                      <span className="mt-3 flex min-w-0 items-center justify-between gap-3">
                        <span className="min-w-0">
                          <span className="block break-words text-sm font-semibold text-gray-800">
                            {theme.label}
                          </span>
                          <span className="mt-0.5 block text-xs leading-4 text-gray-500">
                            {theme.description}
                          </span>
                        </span>
                        <span className="flex shrink-0 gap-1" aria-hidden="true">
                          {theme.swatches.map((color) => (
                            <span
                              key={color}
                              className="h-4 w-4 rounded-full border border-white shadow-sm ring-1 ring-black/5"
                              style={{ backgroundColor: color }}
                            />
                          ))}
                        </span>
                      </span>
                    </label>
                    <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <button
                        type="button"
                        onClick={() => setPreviewThemeId(theme.id)}
                        className="rounded-lg border border-primary/20 bg-white px-3 py-2 text-sm font-semibold text-primary transition hover:bg-primary/5"
                        aria-label={`Посмотреть пример темы ${theme.label}`}
                      >
                        Посмотреть пример
                      </button>
                      {isSelected ? (
                        <span
                          className="rounded-lg bg-primary/10 px-3 py-2 text-center text-xs font-black uppercase tracking-wide text-primary"
                          aria-label={`Сейчас выбрана тема ${theme.label}`}
                        >
                          Выбрана
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => selectTheme(theme.id)}
                          className="rounded-lg bg-gray-900 px-3 py-2 text-sm font-semibold text-white transition hover:bg-gray-800"
                        >
                          Выбрать
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </Section>

          <Section title="Кнопки мессенджеров">
            <div className="-mt-1 min-w-0">
              <p className="text-xs leading-5 text-gray-500">
                Эти кнопки появятся на сайте мастера. Клиент сможет написать мастеру в личные сообщения или позвонить
                по номеру из кнопки «Звонок». Если не хотите показывать канал связи, снимите галочку: кнопка будет
                скрыта даже при заполненном поле.
              </p>
            </div>
            <div className="space-y-3">
              {SITE_MESSENGER_CHANNELS.map((channel) => {
                const button = form.siteMessengerButtons[channel];
                const meta = MESSENGER_META[channel];

                return (
                  <div
                    key={channel}
                    className="rounded-lg border border-gray-100 bg-gray-50 p-3"
                  >
                    <div className="flex min-w-0 items-center justify-between gap-3">
                      <label className="flex min-w-0 items-center gap-3">
                        <input
                          type="checkbox"
                          checked={button.enabled}
                          onChange={(event) => updateMessenger(channel, { enabled: event.target.checked })}
                          className="h-4 w-4 shrink-0 rounded border-gray-300 text-primary focus:ring-primary"
                        />
                        <span
                          className="flex h-9 w-9 shrink-0 overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-black/5"
                          aria-hidden="true"
                        >
                          <MessengerIcon channel={channel} />
                        </span>
                        <span className="truncate text-sm font-semibold text-gray-800">{meta.label}</span>
                      </label>
                      <button
                        type="button"
                        onClick={() => setMessengerHelpChannel(channel)}
                        aria-label={`Где взять ссылку для ${meta.label}`}
                        className="shrink-0 whitespace-nowrap rounded-lg border border-primary/20 bg-white px-2.5 py-1.5 text-xs font-semibold text-primary shadow-sm transition hover:bg-primary/5"
                      >
                        Где взять ссылку?
                      </button>
                    </div>
                    <input
                      type={meta.inputType}
                      value={button.url}
                      onChange={(event) => updateMessenger(channel, { url: event.target.value })}
                      disabled={!button.enabled}
                      aria-label={channel === "phone" ? "Телефон для звонков" : `Ссылка для ${meta.label}`}
                      className="input mt-3 disabled:cursor-not-allowed disabled:bg-white/60 disabled:text-gray-400"
                      placeholder={channel === "phone" && business?.phone ? business.phone : meta.placeholder}
                    />
                  </div>
                );
              })}
            </div>
          </Section>

          <Section title="Фото и сложные правки">
            <p className="text-sm leading-6 text-gray-600">
              Портфолио, новые блоки, крупные визуальные изменения и нестандартную структуру сайта по-прежнему оформляет поддержка. Базовый первый экран и кнопки можно менять здесь самостоятельно.
            </p>
            <button
              type="button"
              onClick={onOpenSupport}
              className="mt-3 inline-flex min-h-10 items-center rounded-lg border border-primary/20 bg-white px-3 py-2 text-sm font-semibold text-primary transition hover:bg-primary/5"
            >
              Написать в поддержку
            </button>
          </Section>

          {error && (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>
          )}
          {saved && (
            <p className="rounded-lg bg-green-50 px-3 py-2 text-sm text-green-700">
              Настройки сайта сохранены
            </p>
          )}

          <button
            type="submit"
            disabled={saveMutation.isPending}
            className="w-full rounded-lg bg-primary py-3 font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
          >
            {saveMutation.isPending ? "Сохранение..." : "Сохранить сайт"}
          </button>
        </div>

        <aside className="hidden min-w-0 lg:block lg:sticky lg:top-20 lg:self-start">
          <div
            className="overflow-hidden rounded-lg shadow-sm"
            style={{ backgroundColor: selectedTheme.swatches[0], color: selectedTheme.text }}
          >
            <div className="p-5" style={{ background: selectedTheme.background }}>
              <p className="text-xs font-black uppercase tracking-[0.18em] opacity-60">
                Предпросмотр
              </p>
              <h3 className="mt-5 break-words text-3xl font-black leading-none">
                {form.tagline || "Заголовок сайта"}
              </h3>
              <p className="mt-4 break-words text-sm leading-6 opacity-75">
                {form.description || "Подзаголовок сайта появится здесь."}
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {enabledButtons.length > 0 ? (
                  enabledButtons.map((channel) => {
                    return (
                      <span
                        key={channel}
                        className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-lg bg-white text-xs font-black shadow-sm"
                      >
                        <MessengerIcon channel={channel} />
                      </span>
                    );
                  })
                ) : (
                  <span className="rounded-lg border border-white/20 bg-white/10 px-3 py-2 text-xs text-white/64">
                    Кнопки скрыты
                  </span>
                )}
              </div>
            </div>
            <div className="p-4 text-[#20151d]" style={{ backgroundColor: selectedTheme.card }}>
              <p className="text-xs font-black uppercase tracking-[0.16em] text-gray-400">
                Запись
              </p>
              <p className="mt-2 text-sm font-semibold">
                Выбрана тема: {selectedTheme.label}
              </p>
            </div>
          </div>
        </aside>
      </form>

      {previewTheme && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/72 p-3 backdrop-blur-sm">
          <button
            type="button"
            className="absolute inset-0 cursor-default"
            aria-label="Закрыть пример"
            onClick={() => setPreviewThemeId(null)}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="theme-preview-title"
            className="relative flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
          >
            <div className="flex flex-col gap-3 border-b border-gray-100 p-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-primary">
                  Пример сайта
                </p>
                <h3 id="theme-preview-title" className="mt-1 text-xl font-black text-gray-900">
                  {previewTheme.label}
                </h3>
                <p className="mt-1 text-sm leading-5 text-gray-500">
                  Так будет выглядеть первый экран сайта в этой цветовой схеме.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setPreviewThemeId(null)}
                className="self-start rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-600 transition hover:bg-gray-50"
              >
                Закрыть
              </button>
            </div>

            <div className="overflow-auto bg-gray-100 p-3 sm:p-5">
              <img
                src={previewTheme.previewImage}
                alt={`Пример сайта: ${previewTheme.label}`}
                className="mx-auto w-full max-w-[1365px] rounded-xl border border-white bg-white shadow-lg"
              />
            </div>

            <div className="flex flex-col gap-2 border-t border-gray-100 p-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm leading-5 text-gray-500">
                Если пример подходит, выберите схему и нажмите «Сохранить сайт».
              </p>
              <button
                type="button"
                onClick={() => {
                  selectTheme(previewTheme.id);
                  setPreviewThemeId(null);
                }}
                className="rounded-lg bg-primary px-4 py-3 text-sm font-black text-white transition hover:opacity-90"
              >
                Выбрать эту схему
              </button>
            </div>
          </div>
        </div>
      )}

      {activeMessengerHelpInstruction && (
        <MessengerHelpDialog
          instruction={activeMessengerHelpInstruction}
          onClose={() => setMessengerHelpChannel(null)}
        />
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0 rounded-lg border border-gray-100 bg-white p-4 shadow-sm">
      <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-600">{title}</h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-xs text-gray-500">{label}</label>
      {children}
    </div>
  );
}

function MessengerHelpDialog({
  instruction,
  onClose,
}: {
  instruction: MessengerLinkInstruction;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/64 p-3 backdrop-blur-sm">
      <button
        type="button"
        className="absolute inset-0 cursor-default"
        aria-label="Закрыть инструкцию"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="messenger-help-title"
        className="relative flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
      >
        <div className="flex items-start justify-between gap-3 border-b border-gray-100 p-4">
          <div className="min-w-0">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-primary">
              Подсказка
            </p>
            <h3 id="messenger-help-title" className="mt-1 text-xl font-black text-gray-900">
              Где взять ссылку: {instruction.title}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-600 transition hover:bg-gray-50"
          >
            Закрыть
          </button>
        </div>

        <div className="overflow-auto p-4">
          <p className="text-sm leading-6 text-gray-600">
            Можно оставить поле пустым: кнопка останется на сайте, но не будет открывать чат. Если канал не нужен,
            снимите галочку.
          </p>

          <div className="mt-4 rounded-lg border border-gray-100 bg-gray-50 p-3">
            <div className="flex gap-3">
              <span
                className="flex h-9 w-9 shrink-0 overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-black/5"
                aria-hidden="true"
              >
                <MessengerIcon channel={instruction.channel} />
              </span>
              <div className="min-w-0">
                <h4 className="text-sm font-black text-gray-900">{instruction.title}</h4>
                <p className="mt-1 text-sm leading-5 text-gray-600">
                  {instruction.description}
                </p>
                <p className="mt-2 break-words rounded-lg bg-white px-3 py-2 text-xs font-semibold text-gray-500 ring-1 ring-gray-100">
                  {instruction.example}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-100 p-4">
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-lg bg-primary px-4 py-3 text-sm font-black text-white transition hover:opacity-90"
          >
            Понятно
          </button>
        </div>
      </div>
    </div>
  );
}
